import React, { useCallback, useState, useMemo, JSX } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { isNumber } from "../../utils/validate";
import {
  uiLanguage,
  userLotUsage,
  userLocationUsage,
  userObjectUsage,
  refreshData,
  userId,
  userExtraFieldsUsage,
  extraFieldsArray,
  useExtraField1,
  useExtraField2,
  useExtraField3,
  useExtraField4,
  useExtraField5,
  userCamera,
  userDepartmentId,
  userScannerMode,
  userFieldsOrder,
  userSearchBarcode,
  userSearchLocation,
  userSearchObject,
  userSearchLot,
  userSearchObject2,
  userSearchLot2,
  userLot2Usage,
  userObject2Usage,
  userExpDateUsage,
  userSeparator,
} from "../../modules/auth/selectors/userSelector";
import { useSelector } from "react-redux";
import { barcodeScanner } from "./formUtils";
import { Button, Alert, Camera2 } from "../index";
import {
  BorderColorOutlined,
  IsoOutlined,
  AddOutlined,
  RemoveOutlined,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { Typography, IconButton, Divider } from "@mui/material";
import { useScannerFormStyles } from "./css";
import { getErrorMessage, getWarningMessage } from "./formUtils";
import { useAuthorizationError, useLoader } from "../../hooks";
import * as modules from "../../utils/modules";
import { getUserValue } from "../../modules/settings/components/utils";
import { ExtraFieldsForm } from "../form";
import { TextField, RowSettings } from "../index";
import {
  OutlinedTextField,
  WhenBarcodeChanges,
  AutocompleteWithFetching,
  OutlinedDatePicker,
} from "./index";
import fieldNames from "../../utils/fields";

interface IScannerForm {
  module: modules.moduleType;
  operationCode?: string;
  operationType?: string;
  userDb: number;
  updateData: (data: any) => void;
  onSubmit: (params: {
    [name: string]: any;
  }) => Promise<{ data: any; warnings: any }>;
}

export interface IScannerFields {
  component:
    | "textField"
    | "autocomplete"
    | "outlinedTextField"
    | "outlinedDatePicker";
  validate?: (value: any) => any;
  name?: string;
  inputMode?: "numeric" | "decimal";
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLInputElement> & { target: HTMLInputElement },
  ) => void;
  autoFocus?: boolean;
  label?: string;
  disabled?: boolean;
  type?: string;
  value?: string | number;
  autoComplete?: "current-password" | "username" | "off" | "new-password";
  required?: boolean;
  endAdornment?: JSX.Element;
  InputProps?: Record<string, any>;
  variant?: "outlined" | "filled" | "standard";
  fillColor?: "grey" | "green" | "white";
  options?: { value?: string | number; label?: string }[];
  valueWhenSelected?: boolean;
  field?:
    | "BARCODE"
    | "OBJECT"
    | "LOT"
    | "LOCATIONPALETTETO"
    | "LOCATIONPALETTEFROM";
  module?: modules.moduleType;
}

const subscription = {
  submitError: true,
  submitting: true,
  errors: true,
};

function ScannerForm(props: IScannerForm) {
  const {
    module,
    operationCode = "",
    operationType,
    userDb,
    updateData,
    onSubmit: userSubmitFunc,
  } = props;

  const moduleParams = useSelector(extraFieldsArray);
  const [logoutUser] = useAuthorizationError();
  const { classes, cx } = useScannerFormStyles();
  const [translate] = useTranslation();
  const locale = useSelector(uiLanguage);
  const scannerMode = useSelector(userScannerMode);
  const useLot = useSelector(userLotUsage);
  const useLocation = useSelector(userLocationUsage);
  const useObject = useSelector(userObjectUsage);
  const useLot2 = useSelector(userLot2Usage);
  const useObject2 = useSelector(userObject2Usage);
  const useExpDate = useSelector(userExpDateUsage);
  const useExtra = useSelector(userExtraFieldsUsage);
  const extra1 = useSelector(useExtraField1);
  const extra2 = useSelector(useExtraField2);
  const extra3 = useSelector(useExtraField3);
  const extra4 = useSelector(useExtraField4);
  const extra5 = useSelector(useExtraField5);
  const fieldsOrder = useSelector(userFieldsOrder);
  const departmentId = useSelector(userDepartmentId);
  const separator = useSelector(userSeparator);
  const inputRefs = React.useRef<HTMLInputElement[]>([]);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [, setLoader] = useLoader();
  const user = useSelector(userId);
  const refresh = useSelector(refreshData);
  const [showExtra, setShowExtra] = useState<{
    show: boolean;
    data: { [key: string]: any };
  }>({ show: false, data: {} });
  const camera = useSelector(userCamera);
  const [expanded, setExpanded] = useState(true);
  const [disabled, setDisabled] = useState<{ [key: string]: boolean }>({});
  const [fieldsParams, setFieldsParams] = useState<{ [name: string]: any }[]>();
  let submitFunction: any;
  let formResetFunction: any;
  const scannerForm = React.useRef<any>(null);
  const [errorMessage, setErrorMessage] = useState({
    message: "",
    error: false,
    status: 0,
  });
  const [scannedData, setScannedData] = useState({
    clientDbId: userDb,
    locale: locale,
    module: module,
    operationType: operationType || "",
    operationCode: operationCode || "",
    barcode: "",
    quantityApp: 0,
    lot: "",
    locationPaletteFrom: "",
    locationPaletteTo: "",
    objectCode: "",
    lot2: "",
    objectCode2: "",
    expDate: "",
    expDate2: "",
  });

  const propsForSettingsButton = {
    locale: locale,
    clientDbId: userDb,
    departmentId: departmentId,
    operationCode: operationCode,
  };

  const barcodeSearch = useSelector(userSearchBarcode);
  const locationSearch = useSelector(userSearchLocation);
  const objectSearch = useSelector(userSearchObject);
  const lotSearch = useSelector(userSearchLot);
  const object2Search = useSelector(userSearchObject2);
  const lot2Search = useSelector(userSearchLot2);

  const lockedFields = JSON.parse(
    window.localStorage.getItem("lockedFields") || "[]",
  );

  const lockedValues = useMemo(
    () => ({
      barcode:
        getUserValue(lockedFields, user, module, operationCode, "barcode") ||
        "",
      quantityApp:
        +getUserValue(
          lockedFields,
          user,
          module,
          operationCode,
          "quantityApp",
        ) || 1,
      objectCode:
        getUserValue(lockedFields, user, module, operationCode, "objectCode") ||
        "",
      locationPaletteFrom:
        getUserValue(
          lockedFields,
          user,
          module,
          operationCode,
          "locationPaletteFrom",
        ) || "",
      locationPaletteTo:
        getUserValue(
          lockedFields,
          user,
          module,
          operationCode,
          "locationPaletteTo",
        ) || "",
      lot: getUserValue(lockedFields, user, module, operationCode, "lot") || "",
      objectCode2:
        getUserValue(
          lockedFields,
          user,
          module,
          operationCode,
          "objectCode2",
        ) || "",
      lot2:
        getUserValue(lockedFields, user, module, operationCode, "lot2") || "",
      expDate:
        getUserValue(lockedFields, user, module, operationCode, "expDate") ||
        "",
      expDate2:
        getUserValue(lockedFields, user, module, operationCode, "expDate2") ||
        "",
    }),
    [lockedFields, user, module, operationCode],
  );

  const initialValues = {
    barcode: "",
    quantityApp: 1,
    objectCode: "",
    locationPaletteFrom: "",
    locationPaletteTo: "",
    lot: "",
  };

  React.useEffect(() => {
    if (lockedValues) {
      const newInitialValues = {
        barcode: lockedValues.barcode,
        quantityApp: lockedValues.quantityApp,
        objectCode: lockedValues.objectCode,
        locationPaletteFrom: lockedValues.locationPaletteFrom,
        locationPaletteTo: lockedValues.locationPaletteTo,
        lot: lockedValues.lot ? String(lockedValues.lot) : "",
        objectCode2: lockedValues.objectCode2,
        lot2: lockedValues.lot2 ? String(lockedValues.lot2) : "",
        expDate: lockedValues.expDate,
        expDate2: lockedValues.expDate2,
      };
      formResetFunction(newInitialValues);
    }
  }, []);

  // fokusavimo funkcija
  const focusFirstEnabledInput = () => {
    if (!inputRefs.current) return;

    for (let i = 0; i < inputRefs.current.length; i++) {
      const input = inputRefs.current[i];

      if (input && !input.disabled && !input.hasAttribute("disabled")) {
        input.focus();
        break;
      }
    }
  };

  React.useEffect(() => {
    focusFirstEnabledInput();
  }, []);

  React.useEffect(() => {
    // Focus after error or warning message is closed
    if (!showAlert) {
      focusFirstEnabledInput();
    }
  }, [showAlert]);

  React.useEffect(() => {
    //If you lock some fields in operation, this info is saved i local storage
    //You can go to menu or settings, info will be saved, until you switch operation
    //If you switch operation, everything from localstorage is deleted
    if (lockedFields.length > 0) {
      const isOpValuesSaved = lockedFields.filter(
        (value: any) =>
          value.module === module &&
          value.user === user &&
          value.operationCode === operationCode,
      );

      if (isOpValuesSaved.length > 0) {
        setDisabled({
          barcode: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "barcode",
          ),
          quantityApp: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "quantityApp",
          ),
          objectCode: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "objectCode",
          ),
          locationPaletteFrom: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "locationPaletteFrom",
          ),
          locationPaletteTo: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "locationPaletteTo",
          ),
          lot: !!getUserValue(lockedFields, user, module, operationCode, "lot"),
          objectCode2: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "objectCode2",
          ),
          lot2: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "lot2",
          ),
          expDate: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "expDate",
          ),
          expDate2: !!getUserValue(
            lockedFields,
            user,
            module,
            operationCode,
            "expDate2",
          ),
        });
      } else {
        window.localStorage.setItem("lockedFields", "[]");
      }
    }
  }, []);

  React.useEffect(
    () => {
      if (
        scannedData &&
        scannedData.barcode !== "" &&
        module === modules.MODULE_PRODUCT
      )
        scan(scannedData);
    }, // eslint-disable-next-line
    [refresh],
  );

  React.useEffect(
    () => {
      const fields = moduleParams
        ? moduleParams.filter(
            (params: any) => params.parameter.toUpperCase() !== "OBJECTS",
          )
        : [];

      setFieldsParams(fields);
    }, // eslint-disable-next-line
    [moduleParams],
  );

  const scan = (params: any) => {
    setLoader(true);

    userSubmitFunc(params)
      .then(({ data, warnings }) => {
        setLoader(false);
        //Case when data has a warning message
        if (warnings && warnings.length > 0) {
          const warningMessage = getWarningMessage(warnings);

          setErrorMessage({
            message: warningMessage,
            error: false,
            status: 0,
          });

          setShowAlert(true);
          return;
        }

        if (data && data.length > 0) {
          updateData(data);
        }
      })
      .catch((err: any) => {
        setLoader(false);
        setShowAlert(true);
        const message = getErrorMessage(err);

        setErrorMessage({
          message: message.message,
          error: true,
          status: message.status,
        });
      })
      .finally(() => {
        const newInitialValues = {
          barcode: lockedValues.barcode,
          quantityApp: lockedValues.quantityApp,
          objectCode: lockedValues.objectCode,
          locationPaletteFrom: lockedValues.locationPaletteFrom,
          locationPaletteTo: lockedValues.locationPaletteTo,
          lot: lockedValues.lot ? String(lockedValues.lot) : "",
          objectCode2: lockedValues.objectCode2,
          lot2: lockedValues.lot2 ? String(lockedValues.lot2) : "",
          expDate: lockedValues.expDate,
          expDate2: lockedValues.expDate2,
        };
        formResetFunction(newInitialValues);
        setShowExtra({
          show: false,
          data: {
            extraInfo1: undefined,
            extraInfo2: undefined,
            extraInfo3: undefined,
            extraInfo4: undefined,
            extraInfo5: undefined,
            note: undefined,
          },
        });
        focusFirstEnabledInput();
      });
  };

  const checkInputUsage = (formFields: IScannerFields[], fieldName: string) =>
    formFields.some((field) => field.name === fieldName);

  const onSubmit = (values: any) => {
    const barcode = barcodeScanner(values.barcode, separator);
    const quantity =
      values.quantityApp && typeof values.quantityApp === "string"
        ? values.quantityApp.replace(",", ".")
        : values.quantityApp;

    let params = {
      ...scannedData,
      clientDbId: userDb,
      module: module,
      operationType: operationType || "",
      operationCode: operationCode || "",
      barcode: barcode.barcode,
      quantityApp: quantity,
      locationPaletteFrom:
        checkInputUsage(scannerFormFields, "locationPaletteFrom") &&
        (useLocation || module === modules.MODULE_LOCATION)
          ? values.locationPaletteFrom || ""
          : undefined,
      locationPaletteTo:
        checkInputUsage(scannerFormFields, "locationPaletteTo") &&
        (useLocation || module === modules.MODULE_LOCATION)
          ? values.locationPaletteTo || ""
          : undefined,
      lot: useLot ? String(values.lot || "") : "",
      lot2: useLot2 ? String(values.lot2 || "") : "",
      objectCode: useObject ? values.objectCode || "" : undefined,
      objectCode2: useObject2 ? values.objectCode2 || "" : undefined,
      expDate: useExpDate ? values.expDate : undefined,
      expDate2:
        useLocation || module === modules.MODULE_LOCATION
          ? values.expDate2
          : undefined,
      ...showExtra.data,
      scannedWithCamera: camera,
      departmentId: departmentId,
    };

    // Jeigu vedant i barkodo lauka naudojamas skyriklis, isskirti objekta, serija ar kieki
    // Tada informacija siunciama i backend net jei laukai ir neijungti
    if (barcode.kodasOs) {
      params = {
        ...params,
        objectCode: barcode.kodasOs,
      };
    }

    if (barcode.serija) {
      params = {
        ...params,
        lot: barcode.serija,
      };
    }

    if (barcode.amount) {
      params = {
        ...params,
        quantityApp: barcode.amount,
      };
    }

    setScannedData(params);
    scan(params);
  };

  const handleCancelAlert = () => {
    logoutUser(errorMessage.status);

    if (errorMessage.status !== 401) {
      setShowAlert(false);
    }
  };

  const warningId = localStorage.getItem("warningId");

  const handleContinue = () => {
    const params = { ...scannedData, ignoreWarnings: 1, warningId };
    localStorage.removeItem("warningId");
    setShowAlert(false);
    scan(params);
  };

  const handleDisassemble = () => {
    const params = { ...scannedData, ignoreWarnings: 1, warningId };
    localStorage.removeItem("warningId");
    setShowAlert(false);
    scan(params);
  };

  const handleDisassembleAndUpload = () => {
    const params = { ...scannedData, ignoreWarnings: 2, warningId };
    localStorage.removeItem("warningId");
    setShowAlert(false);
    scan(params);
  };

  const handleUploadAll = () => {
    const params = { ...scannedData, ignoreWarnings: 3, warningId };
    localStorage.removeItem("warningId");
    setShowAlert(false);
    scan(params);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Enter" && scannerMode !== "autoMode") {
      event.preventDefault();

      // Find the next enabled input
      let nextIndex = index + 1;
      while (
        nextIndex < inputRefs.current.length &&
        inputRefs.current[nextIndex].disabled
      ) {
        nextIndex++;
      }

      // Focus the next enabled input or the first one if none found
      if (nextIndex < inputRefs.current.length) {
        inputRefs.current[nextIndex].focus();
      } else if (buttonRef.current) {
        buttonRef.current.focus();
      } else submitFunction();
    }
  };

  const insertMinusSign = useCallback(() => {
    if (scannerForm.current) {
      const fieldState = scannerForm.current.getFieldState("quantityApp");
      const value = fieldState && fieldState.value ? -fieldState.value : -1;
      scannerForm.current.change("quantityApp", value);

      if (inputRefs.current) inputRefs.current[1].focus();
    }
  }, [scannerForm, inputRefs]);

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    event.target.select();
  };

  const handleExtraFields = () => {
    setShowExtra({ ...showExtra, show: true });
  };

  const handleCloseExtra = () => {
    setShowExtra({ ...showExtra, show: false });
  };

  const handleConfirmExtra = (extraFields: Record<string, unknown>) => {
    setShowExtra({ show: false, data: extraFields });
  };

  const valueToShow = React.useMemo(() => {
    let stringToShow = ``;
    const extra = [extra1, extra2, extra3, extra4, extra5];

    const show = extra.map((field, index) =>
      field.type === "$B" &&
      typeof showExtra.data["extraInfo" + (index + 1)] !== "undefined"
        ? translate(showExtra.data["extraInfo" + (index + 1)])
        : showExtra.data["extraInfo" + (index + 1)],
    );

    if (showExtra.data.note) stringToShow += `${showExtra.data.note} `;

    if (show.length > 0)
      show.forEach((field) =>
        typeof field !== "undefined"
          ? (stringToShow += (stringToShow !== `` ? `|` : ``) + `${field} `)
          : ``,
      );

    return stringToShow;
  }, [showExtra.data, extra1, extra2, extra3, extra4, extra5, translate]);

  const handleCameraScanning = (result: any) => {
    if (result && result.isScanSuccess) {
      let activeEl: string | undefined;
      try {
        activeEl = document.activeElement?.id; // Nustatome aktyvų elementą
      } catch {
        activeEl = "";
      }

      // Tikriname, kuris laukelis yra aktyvus, ir priskiriame nuskenuotą reikšmę
      if (activeEl === "objectCode") {
        scannerForm.current.change("objectCode", result.data);
      } else if (activeEl === "lot") {
        scannerForm.current.change("lot", result.data);
      } else if (activeEl === "objectCode2") {
        scannerForm.current.change("objectCode2", result.data);
      } else if (activeEl === "lot2") {
        scannerForm.current.change("lot2", result.data);
      } else {
        // Kitais atvejais priskiriame reikšmę "barcode" laukeliui
        scannerForm.current.change("barcode", result.data);
      }

      if ("vibrate" in navigator) {
        // vibration API supported
        window.navigator.vibrate(300);
      }

      if (scannerMode !== "autoMode") scannerForm.current.submit();
    }
  };

  const handleAddition = useCallback((op: "add" | "subtract") => {
    const form = scannerForm.current;
    if (form) {
      const fieldState = form.getFieldState("quantityApp");
      if (!fieldState) return;

      const currentValue = Number.parseFloat(String(fieldState.value ?? 0));
      const value =
        (Number.isFinite(currentValue) ? currentValue : 0) +
        (op === "add" ? 1 : -1);

      form.change("quantityApp", value);
      if (inputRefs.current) inputRefs.current[1].focus();
    }
  }, []);

  const expandAccordion = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const lockTheInput = useCallback(
    (field: string, lock: boolean) => {
      setDisabled((prev) => ({ ...prev, [field]: lock }));
      let exists = false;
      const fieldState = scannerForm.current?.getFieldState(field);
      const fieldValue = fieldState?.value ? "" + fieldState.value : "";
      let newLS = [];

      if (lock) {
        newLS =
          lockedFields && Array.isArray(lockedFields)
            ? lockedFields.map((obj) => {
                if (
                  obj.user === user &&
                  obj.module === module &&
                  obj.operationCode === operationCode &&
                  obj.field === field
                ) {
                  exists = true;
                  return { ...obj, value: fieldValue };
                }

                return obj;
              })
            : [];

        if (!exists)
          newLS = [
            ...lockedFields,
            {
              user: user,
              module: module,
              operationCode: operationCode,
              field: field,
              value: fieldValue,
            },
          ];
      } else {
        newLS =
          lockedFields && Array.isArray(lockedFields)
            ? lockedFields.filter(
                (obj) =>
                  !(
                    obj.user === user &&
                    obj.module === module &&
                    obj.operationCode === operationCode &&
                    obj.field === field
                  ),
              )
            : [];
      }

      window.localStorage.setItem("lockedFields", JSON.stringify(newLS));
    },
    [user, module, operationCode, lockedFields],
  );

  const scannerFormFields = React.useMemo(() => {
    const fields: IScannerFields[] = [];

    fieldsOrder.forEach((fieldKey: any) => {
      switch (fieldKey) {
        case fieldNames.BARCODE:
          fields.push(
            barcodeSearch === "S"
              ? {
                  component: "autocomplete",
                  name: "barcode",
                  label: translate("barcode"),
                  required: true,
                  field: "BARCODE",
                  module,
                }
              : {
                  component: "outlinedTextField",
                  name: "barcode",
                  label: translate("barcode"),
                  required: true,
                },
          );
          break;

        case fieldNames.QUANTITY:
          if (module !== modules.MODULE_PRODUCT)
            fields.push({
              component: "outlinedTextField",
              name: "quantityApp",
              label: translate("quantity"),
              required: true,
              validate: isNumber,
              InputProps: {
                inputProps: {
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                },
              },
            });
          break;

        case fieldNames.OBJECT:
          if (useObject && module !== modules.MODULE_PRODUCT)
            fields.push(
              objectSearch === "S"
                ? {
                    component: "autocomplete",
                    name: "objectCode",
                    label: translate("object"),
                    required: true,
                    field: "OBJECT",
                    module,
                  }
                : {
                    component: "outlinedTextField",
                    name: "objectCode",
                    label: translate("object"),
                  },
            );
          break;

        case fieldNames.LOCATION:
          if (
            module !== modules.MODULE_PRODUCT &&
            (module === modules.MODULE_LOCATION || useLocation)
          )
            fields.push(
              locationSearch === "S"
                ? {
                    component: "autocomplete",
                    name: "locationPaletteFrom",
                    label: translate("locationPaletteFrom"),
                    required: true,
                    field: "LOCATIONPALETTEFROM",
                    module,
                  }
                : {
                    component: "outlinedTextField",
                    name: "locationPaletteFrom",
                    label: translate("locationPaletteFrom"),
                  },
            );
          break;

        case fieldNames.LOCATION2:
          if (
            module !== modules.MODULE_PRODUCT &&
            (module === modules.MODULE_LOCATION || useLocation)
          )
            fields.push(
              locationSearch === "S"
                ? {
                    component: "autocomplete",
                    name: "locationPaletteTo",
                    label: translate("locationPaletteTo"),
                    required: true,
                    field: "LOCATIONPALETTETO",
                    module,
                  }
                : {
                    component: "outlinedTextField",
                    name: "locationPaletteTo",
                    label: translate("locationPaletteTo"),
                  },
            );
          break;

        case fieldNames.OBJECT2:
          if (useObject2 && module !== modules.MODULE_PRODUCT)
            fields.push(
              object2Search === "S"
                ? {
                    component: "autocomplete",
                    name: "objectCode2",
                    label: translate("object2"),
                    required: true,
                    field: "OBJECT",
                    module,
                  }
                : {
                    component: "outlinedTextField",
                    name: "objectCode2",
                    label: translate("object2"),
                  },
            );
          break;

        case fieldNames.LOT:
          if (useLot && module !== modules.MODULE_PRODUCT)
            fields.push(
              lotSearch === "S"
                ? {
                    component: "autocomplete",
                    name: "lot",
                    label: translate("lot"),
                    required: true,
                    field: "LOT",
                    module,
                  }
                : {
                    component: "outlinedTextField",
                    name: "lot",
                    label: translate("lot"),
                  },
            );
          break;

        case fieldNames.LOT2:
          if (useLot2 && module !== modules.MODULE_PRODUCT)
            fields.push(
              lot2Search === "S"
                ? {
                    component: "autocomplete",
                    name: "lot2",
                    label: translate("lot2"),
                    required: true,
                    field: "LOT",
                    module,
                  }
                : {
                    component: "outlinedTextField",
                    name: "lot2",
                    label: translate("lot2"),
                  },
            );
          break;

        case fieldNames.EXPDATE:
          if (useExpDate && module !== modules.MODULE_PRODUCT)
            fields.push({
              component: "outlinedDatePicker",
              name: "expDate",
              label: translate("expDate"),
            });
          break;

        case fieldNames.EXPDATE2:
          if (
            module !== modules.MODULE_PRODUCT &&
            (module === modules.MODULE_LOCATION || useLocation)
          )
            fields.push({
              component: "outlinedDatePicker",
              name: "expDate2",
              label: translate("expDate2"),
            });
          break;

        case fieldNames.EXTRA:
          if (useExtra && module !== modules.MODULE_PRODUCT)
            fields.push({
              component: "textField",
              label: translate("exInfo"),
              endAdornment: (
                <IconButton onClick={handleExtraFields}>
                  <BorderColorOutlined fontSize="small" />
                </IconButton>
              ),
              value: valueToShow,
              disabled: true,
            });
          break;

        default:
          break;
      }
    });

    return fields;
  }, [
    fieldsOrder,
    barcodeSearch,
    useObject,
    useLocation,
    useObject2,
    useLot,
    useLot2,
    useExpDate,
    useExtra,
    translate,
    module,
    classes,
    handleAddition,
    valueToShow,
  ]);

  return (
    <div className={classes.container}>
      {showAlert && (
        <Alert
          message={errorMessage.message}
          isOpen={showAlert}
          onClose={handleCancelAlert}
          isConfirmationButton={!errorMessage.error}
          onConfirm={handleContinue}
          onConfirmDisassemble={handleDisassemble}
          onConfirmDisassembleAndUpload={handleDisassembleAndUpload}
          onConfirmUploadAll={handleUploadAll}
          iconColor={!errorMessage.error ? "warning" : "error"}
        />
      )}

      {showExtra.show && (
        <ExtraFieldsForm
          open={showExtra.show}
          onClose={handleCloseExtra}
          fieldsList={fieldsParams}
          onConfirm={handleConfirmExtra}
          initialValues={showExtra.data}
        />
      )}

      <div className={cx({ [classes.hide]: !expanded })}>
        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          subscription={subscription}
          render={({ handleSubmit, submitError, form, errors }) => {
            submitFunction = handleSubmit;
            formResetFunction = form.reset;
            scannerForm.current = form;
            return (
              <form
                onSubmit={async (event) => {
                  const error = await handleSubmit(event);
                  if (error) {
                    return error;
                  }
                  form.reset();
                }}
                method="post"
                className={classes.fields}
              >
                {submitError && (
                  <Typography color="error">{submitError}</Typography>
                )}

                {camera && <Camera2 onUpdate={handleCameraScanning} />}

                {scannerFormFields.map((field, index) => {
                  const { component, ...rest } = field;
                  const restProps = {
                    ...rest,
                    variant: "filled",
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (rest.name === "quantityApp" && e.key === " ") {
                        e.preventDefault();
                      }
                      handleKeyPress(e, index);
                    },
                    onFocus: handleFocus,
                    fillColor: "white",
                  };

                  return (
                    <div className={classes.row} key={index}>
                      {component === "autocomplete" && (
                        //@ts-ignore
                        <AutocompleteWithFetching
                          ref={(item: any) => (inputRefs.current[index] = item)}
                          disabled={
                            (field.name && disabled[field.name]) || false
                          }
                          InputProps={{
                            disableUnderline: true,
                            sx: { borderRadius: "4px 0 0 4px" },
                          }}
                          scannerFormFields={scannerFormFields}
                          {...restProps}
                        />
                      )}

                      {component === "outlinedTextField" && (
                        //@ts-ignore
                        <OutlinedTextField
                          type="text"
                          autoComplete="off"
                          ref={(item: any) => (inputRefs.current[index] = item)}
                          disabled={
                            (field.name && disabled[field.name]) || false
                          }
                          endAdornment={
                            field.name === "quantityApp" ? (
                              <div className={classes.plusMinusButtons}>
                                <IconButton
                                  disabled={disabled.quantityApp}
                                  onClick={() => handleAddition("subtract")}
                                >
                                  <RemoveOutlined />
                                </IconButton>

                                <IconButton
                                  disabled={disabled.quantityApp}
                                  onClick={() => handleAddition("add")}
                                >
                                  <AddOutlined />
                                </IconButton>
                              </div>
                            ) : undefined
                          }
                          {...restProps}
                          InputProps={{
                            ...restProps.InputProps,
                            id: field.name,
                            disableUnderline: true,
                            sx: {
                              ...restProps.InputProps?.sx,
                              borderRadius: "4px 0 0 4px",
                            },
                          }}
                        />
                      )}

                      {component === "textField" && (
                        //@ts-ignore
                        <TextField
                          type="text"
                          autoComplete="off"
                          InputProps={{
                            disableUnderline: true,
                          }}
                          {...restProps}
                        />
                      )}

                      {component === "outlinedDatePicker" && (
                        //@ts-ignore
                        <OutlinedDatePicker
                          ref={(item: any) => (inputRefs.current[index] = item)}
                          disabled={
                            (field.name && disabled[field.name]) || false
                          }
                          InputProps={{
                            disableUnderline: true,
                            sx: { borderRadius: "4px 0 0 4px" },
                          }}
                          {...restProps}
                        />
                      )}

                      {field.name === "quantityApp" && (
                        <IconButton
                          classes={{ root: classes.iconButton0 }}
                          disabled={disabled.quantityApp}
                          onClick={insertMinusSign}
                        >
                          <IsoOutlined />
                        </IconButton>
                      )}

                      {module !== modules.MODULE_PRODUCT && field.name && (
                        <RowSettings
                          module={module}
                          inputRef={inputRefs}
                          index={index}
                          propsForQuery={propsForSettingsButton}
                          onInputLock={lockTheInput}
                          scannerFormFields={scannerFormFields}
                        />
                      )}
                    </div>
                  );
                })}

                <WhenBarcodeChanges
                  field="barcode"
                  fieldAmount="quantityApp"
                  fieldLot="lot"
                  fieldObject="objectCode"
                />

                <div>
                  <Button
                    display={scannerMode !== "default" ? "none" : undefined}
                    type={scannerMode === "autoMode" ? "submit" : undefined}
                    ref={buttonRef}
                    disabled={
                      typeof errors === "object" &&
                      Object.keys(errors).length !== 0
                    }
                    onClick={async (event) => {
                      const submitError = await handleSubmit(event);
                      if (submitError) {
                        return submitError;
                      }
                      form.reset();
                      form.change("lot", "");
                      form.change("quantityApp", 1);
                      form.change("objectCode", "");
                    }}
                    fullWidth={true}
                  >
                    {translate("continue", "Tęsti")}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      </div>

      {module !== modules.MODULE_PRODUCT && (
        <div className={classes.hideContainer}>
          <Divider className={classes.hideDividers} />
          <div onClick={expandAccordion} className={classes.hideButton}>
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            {expanded ? translate("hide") : translate("expand")}
          </div>
          <Divider className={classes.hideDividers} />
        </div>
      )}
    </div>
  );
}

export default ScannerForm;
